import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DisInput from '../components/design-system/DisInput';
import DisButton from '../components/design-system/DisButton';
import DisCard from '../components/design-system/DisCard';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  const token = searchParams.get('token');

  useEffect(() => {
    console.log('🔍 ResetPasswordPage chargé avec token:', token);
    console.log('🔍 URL complète:', window.location.href);

    if (!token) {
      setTokenValid(false);
      setError('Lien de réinitialisation invalide ou manquant 🔗');
    } else {
      // Valider le token immédiatement
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      await api.get(`/auth/validate-reset-token/${token}`);
      setTokenValid(true);
    } catch (error) {
      console.error('Token validation error:', error);
      setTokenValid(false);
      if (error.response?.status === 401) {
        setError('Lien de réinitialisation expiré ou invalide ⏰');
      } else {
        setError('Erreur lors de la validation du lien 🔗');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validation
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères 🔐');
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas 🔄');
      setIsLoading(false);
      return;
    }

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: formData.password
      });
      setIsSuccess(true);
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.response?.status === 401) {
        setError('Lien de réinitialisation expiré ou invalide ⏰');
        setTokenValid(false);
      } else if (error.response?.status === 400) {
        setError('Mot de passe invalide. Utilisez au moins 6 caractères 📝');
      } else {
        setError('Erreur lors de la réinitialisation. Veuillez réessayer 🔄');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="Dis Maman Logo" 
              className="w-20 h-20 rounded-2xl shadow-lg"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>Dis Maman !</h1>
          <p className="text-white text-lg" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>Nouveau mot de passe 🔑</p>
        </div>

        <DisCard padding="lg" className="!bg-transparent backdrop-blur-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
          {!tokenValid ? (
            <div className="text-center space-y-6">
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-red-600 mb-4">
                Lien invalide
              </h2>
              <p className="text-gray-600 mb-6">
                Ce lien de réinitialisation est invalide ou a expiré.
                Veuillez en demander un nouveau.
              </p>
              <DisButton
                onClick={handleBackToLogin}
                className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700"
              >
                Retour à la connexion
              </DisButton>
            </div>
          ) : !isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  🔐 Nouveau mot de passe
                </h2>
                <p className="text-gray-600 text-center mb-6">
                  Choisissez un nouveau mot de passe sécurisé pour votre compte.
                </p>
              </div>

              <DisInput
                label="🔒 Nouveau mot de passe"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Au moins 6 caractères"
                required
              />

              <DisInput
                label="🔒 Confirmer le mot de passe"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Retapez votre mot de passe"
                required
              />

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
                💡 Votre mot de passe doit contenir au moins 6 caractères
              </div>

              <DisButton
                type="submit"
                loading={isLoading}
                disabled={isLoading || !formData.password || !formData.confirmPassword}
                className="w-full text-lg py-4 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
              >
                {isLoading ? '🔄 Mise à jour...' : '✅ Mettre à jour mon mot de passe'}
              </DisButton>
            </form>
          ) : (
            <div className="text-center space-y-6">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-green-600 mb-4">
                Mot de passe mis à jour !
              </h2>
              <p className="text-gray-600 mb-6">
                Votre mot de passe a été mis à jour avec succès.
                Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
              <DisButton
                onClick={handleBackToLogin}
                className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
              >
                🚀 Me connecter maintenant
              </DisButton>
            </div>
          )}
        </DisCard>

        <div className="mt-6 text-center">
          <p className="text-white text-sm opacity-75">
            Besoin d'aide ? Contactez-nous à contact@dismaman.com 💌
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;